import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/Mypage.scss';
import Loading from '../../../../components/Loading';
import MypageMenu from '../../common/MypageMenu';
import { scoutApi, errorMessage, type JobSummary, type TalentItem } from '../../applications/applicationStatusApi';
import { ConfirmDialog } from '../../applications/applicationUi';
import '../../applications/applications.scss';

// 교회 인재 검색 (application-status-design.md 7장)
// 스카우트 제안에 동의한 이력서만, 이름은 마스킹. 적합도는 공고의 정형 필드 기준 (스키마 설계서 7장)
export default function TalentSearch() {
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const recruitId = params.get('id') || '';
  const recruitType = params.get('tableType') === 'overseas' ? 'overseas' : 'minister';

  const [job, setJob] = useState<JobSummary | null>(null);
  const [items, setItems] = useState<TalentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [target, setTarget] = useState<TalentItem | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await scoutApi.talent(recruitType, recruitId);
      setJob(data.job);
      setItems(data.items);
      setError('');
    } catch (e) {
      setError(errorMessage(e, '인재 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [recruitId, recruitType]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await scoutApi.send(recruitType, recruitId, target.resumeId, message.trim() || undefined);
      setTarget(null);
      setMessage('');
      load();
    } catch (e) {
      setTarget(null);
      alert(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            <div className="app-board-head">
              <div className="main_title_row">
                <h3>인재 검색{job ? ` — ${job.title}` : ''}</h3>
                <div className="list-btn" onClick={() => navigate(`/mypage/applicantmanage?id=${recruitId}&tableType=${recruitType}`)}>
                  <span>지원자 관리</span>
                </div>
              </div>
              <p>
                조건에 맞는 사역자 {items.length}명 · 스카우트 제안에 동의한 이력서만 표시됩니다. 이름은 가려져 있으며, 상대가 제안을 수락해 지원한 뒤 연락 단계에서 실명이 공개됩니다.
              </p>
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <Loading />
              </div>
            ) : error ? (
              <div className="app-empty">
                <p>{error}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="app-empty">
                <p>아직 스카우트 제안에 동의한 사역자가 없습니다.</p>
              </div>
            ) : (
              <div className="app-list">
                {items.map((t) => {
                  const profile = [t.gender, t.age ? `${t.age}세` : ''].filter(Boolean).join('·');
                  const info = [
                    t.region && (t.prefs.location ? `${t.region} → ${t.prefs.location} 희망` : t.region),
                    t.careerYears ? `경력 ${t.careerYears}년` : '',
                    t.education,
                  ].filter(Boolean).join(' · ');
                  const prefs = [t.prefs.roles, t.prefs.depts, t.prefs.type].filter(Boolean).join(' / ');
                  return (
                    <div key={t.resumeId} className="app-board-row">
                      <div className="app-board-row__top">
                        <strong>{t.name}</strong>
                        {profile && <span className="app-board-row__sub">{profile}</span>}
                        <span className="app-status-badge app-status-badge--highlight">적합도 {t.score}</span>
                      </div>
                      {info && <div className="app-board-row__info">{info}</div>}
                      {prefs && <div className="app-board-row__info">희망: {prefs}</div>}
                      {t.reasons.length > 0 && <div className="app-board-row__info" style={{ color: 'var(--ink-3)' }}>근거: {t.reasons.join(' · ')}</div>}
                      <div className="app-board-row__buttons">
                        {t.offerStatus ? (
                          <span className="app-status-badge app-status-badge--muted">
                            {t.offerStatus === 'PENDING' ? '제안 보냄 · 응답 대기' : t.offerStatus === 'DECLINED' ? '제안 거절됨' : '제안 수락됨'}
                          </span>
                        ) : (
                          <button type="button" className="app-btn app-btn--small app-btn--accent" onClick={() => setTarget(t)}>
                            제안 보내기
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {target && (
        <ConfirmDialog title={`${target.name}님에게 지원을 제안할까요?`} confirmLabel="제안 보내기" busy={busy} onConfirm={send} onCancel={() => setTarget(null)}>
          <p>사역자에게 알림이 전송되고, 수락하면 이 공고에 지원이 접수됩니다.</p>
          <textarea rows={3} maxLength={1000} value={message} placeholder="제안 메시지 (선택) — 예: 청년부 담당으로 모시고 싶습니다." onChange={(e) => setMessage(e.target.value)} />
        </ConfirmDialog>
      )}
    </div>
  );
}
